import { useEffect, useId, useRef, useState } from "react";
import Icon from "../Icon";
import useTranslations from "../../../core/i18n/useTranslations";
import { Row, Spacer } from "../../../components/Layout/Layout";
import PrimeTooltip from "../PrimeTooltip/PrimeTooltip";

import styles from "./CopyToClipboard.module.css";

export const CopyToClipboard = ({
  prefilledText,
  text,
}: {
  text: string;
  prefilledText?: string;
}) => {
  const t = useTranslations();
  const tooltipId = `copy-${useId().replace(/:/g, "")}`;
  const iconWrapperRef = useRef<HTMLSpanElement>(null);

  const [isCopied, setIsCopied] = useState(false);
  const [targetReady, setTargetReady] = useState(false);

  useEffect(() => {
    const wrapper = iconWrapperRef.current;
    if (!wrapper) return;

    const markReady = () => {
      if (wrapper.querySelector("svg")) {
        setTargetReady(true);
      }
    };

    markReady();
    if (wrapper.querySelector("svg")) return;

    const observer = new MutationObserver(markReady);
    observer.observe(wrapper, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  const delay = (time: number) =>
    new Promise((resolve) => setTimeout(resolve, time));

  if (!("clipboard" in navigator)) return;

  return (
    <>
      <span id={tooltipId} ref={iconWrapperRef}>
        <Icon
          className={styles.copyIcon}
          name="document-duplicate"
          onClick={async () => {
            setIsCopied(true);
            await navigator.clipboard.writeText(
              prefilledText ? `${prefilledText}${text}` : text,
            );
            await delay(1000);
            setIsCopied(false);
          }}
          size={18}
          color="Primary-700"
          hoverEffect
        />
      </span>
      <PrimeTooltip
        target={targetReady ? `#${tooltipId}` : undefined}
        theme={isCopied ? "dark" : "light"}
        content={
          isCopied ? (
            <Row>
              <Icon name="check-circle" size={16} />
              <Spacer size={6} />
              {t("Common:copied")}
            </Row>
          ) : (
            t("Common:copy_to_clipboard")
          )
        }
      />
    </>
  );
};
